/**
 * @file: This file writes a length of time the way the app shows it.
 *
 * A length is minutes and seconds, and hours in front of them when there are
 * any. The `duration` filter shows a length in a template, and the sentences of
 * the Compose view put one in a line of text. The two wrote the same arithmetic
 * twice, and an hour came out differently in each.
 *
 * The report needs two more forms of the same length: one that a spreadsheet
 * reads as a time, and one that drops the seconds. They are here, with the
 * arithmetic they share.
 */

const SECOND_MS = 1000
const MINUTE_SECONDS = 60
const HOUR_SECONDS = 60 * MINUTE_SECONDS
const MINUTE_MS = MINUTE_SECONDS * SECOND_MS
const HOUR_MINUTES = 60

const pad = value => String(value).padStart(2, '0')

/**
 * The text for a value that is no length, or null when the value is one.
 *
 * An empty value gives an empty string, because a template shows nothing for a
 * length that is not there yet. A value that is not a number says so, so a
 * fault is visible instead of shown as zero. All three forms of a length
 * answer the same way for the same value.
 *
 * @param {number|string} ms - The value to test
 * @returns {string|null} The text to show, or null
 */
function badLength (ms) {
  if (ms == null) {
    return ''
  }

  // parseFloat refuses a boolean, and isFinite refuses a string that only
  // starts with a number. Both were in the filter before this file, and both
  // are needed. The values come from a template.
  if (isNaN(parseFloat(ms)) || !isFinite(ms)) {
    return 'NaN'
  }

  return null
}

/**
 * Milliseconds as a clock: `m:ss`, or `h:mm:ss` from one hour.
 *
 * @param {number|string} ms - The length in milliseconds
 * @returns {string} The length as a clock
 */
function formatDuration (ms) {
  const bad = badLength(ms)

  if (bad !== null) {
    return bad
  }

  const total = Math.floor(Math.abs(Number(ms)) / SECOND_MS)
  const hours = Math.floor(total / HOUR_SECONDS)
  const minutes = Math.floor((total % HOUR_SECONDS) / MINUTE_SECONDS)
  const seconds = total % MINUTE_SECONDS

  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`
}

/**
 * Milliseconds as a clock with the hour field always written: `h:mm:ss`.
 *
 * Excel reads `4:32` as four hours, so the export writes the hour even when it
 * is zero. `formatDuration` drops the hour under one hour, so the export
 * cannot use it.
 *
 * @param {number} ms - The length in milliseconds
 * @returns {string} The length as `h:mm:ss`
 */
function formatClock (ms) {
  const bad = badLength(ms)

  if (bad !== null) {
    return bad
  }

  const total = Math.floor(Math.abs(Number(ms)) / SECOND_MS)
  const hours = Math.floor(total / HOUR_SECONDS)
  const minutes = Math.floor((total % HOUR_SECONDS) / MINUTE_SECONDS)

  return `${hours}:${pad(minutes)}:${pad(total % MINUTE_SECONDS)}`
}

/**
 * Milliseconds as a coarse length: `2 h 5 min`, or `29 min` under one hour.
 *
 * The seconds of a pause of half an hour are noise, so the tiles and the
 * findings of the report show the minutes only.
 *
 * @param {number} ms - The length in milliseconds
 * @returns {string} The length in hours and minutes
 */
function formatCoarse (ms) {
  const bad = badLength(ms)

  if (bad !== null) {
    return bad
  }

  const minutes = Math.round(Math.abs(Number(ms)) / MINUTE_MS)
  const hours = Math.floor(minutes / HOUR_MINUTES)

  return hours > 0 ? `${hours} h ${minutes % HOUR_MINUTES} min` : `${minutes} min`
}

export { formatDuration, formatClock, formatCoarse }
